import { darken } from 'polished';
import React from 'react';
import styled from 'styled-components';
import { Avatar, Icon, MemberContactPopover, Tooltip } from '~/components';
import { colors, weights } from '~/styles';

const Stack = styled.div`
  display: flex;
  align-items: center;
  /* The overlap below is ordered with z-index; contain it here so it can't outrank a sticky column and ride
     over it when the list scrolls sideways. */
  isolation: isolate;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -0.3rem;
  right: -0.3rem;
  z-index: 1;
  display: none;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: ${colors.danger};
  color: ${colors.white};
  font-weight: ${weights.normal};
  font-size: 0.6rem;
  cursor: pointer;
  box-shadow: 0 0 0 1px ${colors.white};

  &:hover {
    background: ${darken(0.06, colors.danger)};
    color: ${colors.white};
  }
`;

const Item = styled.div`
  position: relative;
  z-index: ${({ $z }) => $z};

  &:not(:first-child) {
    margin-left: -0.35rem;
  }

  figure {
    box-shadow: 0 0 0 2px ${colors.white};
    opacity: ${({ $dim }) => ($dim ? 0.4 : 1)};
  }

  &:hover ${RemoveButton} {
    display: flex;
  }
`;

const Overflow = styled.div`
  position: relative;
  margin-left: -0.35rem;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  color: ${colors.grey40};
  background-color: ${colors.grey5};
  box-shadow: 0 0 0 2px ${colors.white};
`;

export default function AvatarStack({ members, max = 5, size = '1.75rem', onRemove }) {
  if (!members?.length) return null;

  const visible = members.length > max ? members.slice(0, max) : members;
  const overflow = members.length > max ? members.slice(max) : [];

  return (
    <Stack>
      {visible.map((member, index) => (
        <Item key={member.id ?? index} $z={members.length - index} $dim={member.isInactive}>
          <MemberContactPopover member={member} placement="left">
            <Avatar value={member} isCircle hasBackground size={size} initialsFontSize="0.7rem" />
          </MemberContactPopover>
          {onRemove && (
            <RemoveButton
              type="button"
              aria-label={`Unassign ${member.name}`}
              onClick={(event) => {
                event.stopPropagation();
                onRemove(member);
              }}>
              <Icon icon="times" />
            </RemoveButton>
          )}
        </Item>
      ))}

      {overflow.length > 0 && (
        <Tooltip maxWidth="20rem" message={overflow.map((member) => member.name).join(', ')}>
          <Overflow $size={size}>+{overflow.length}</Overflow>
        </Tooltip>
      )}
    </Stack>
  );
}
